import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Card } from './Card';
import { config } from '../../config';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  onReset?: () => void;
  showHomeButton?: boolean;
  title?: string;
  message?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary component that catches JavaScript errors anywhere in the child
 * component tree and displays a fallback UI instead of crashing the whole app.
 */
class ErrorBoundaryClass extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    // Auto-reload on stale chunk 404 (happens after a new deploy)
    if (error.message?.includes('Failed to fetch dynamically imported module') ||
        error.message?.includes('Importing a module script failed')) {
      window.location.reload();
      return;
    }

    if (config.isDev) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    this.props.onError?.(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    this.props.onReset?.();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
          onGoHome={this.handleGoHome}
          showHomeButton={this.props.showHomeButton}
          title={this.props.title}
          message={this.props.message}
        />
      );
    }

    return this.props.children;
  }
}

interface ErrorFallbackProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onReset: () => void;
  onGoHome: () => void;
  showHomeButton?: boolean;
  title?: string;
  message?: string;
}

function ErrorFallback({
  error,
  errorInfo,
  onReset,
  onGoHome,
  showHomeButton = true,
  title = 'Něco se pokazilo',
  message = 'Omlouváme se, ale došlo k neočekávané chybě. Zkuste obnovit stránku nebo se vraťte na hlavní stránku.',
}: ErrorFallbackProps) {
  const isDev = config.isDev;

  return (
    <div className="flex justify-center items-center min-h-[400px] p-6">
      <Card variant="glass" padding="none" className="max-w-[600px] w-full p-8 text-center">
        {/* Error Icon */}
        <div className="w-20 h-20 rounded-full flex items-center justify-center bg-gradient-to-br from-red-500/10 to-orange-500/10 mx-auto mb-6">
          <AlertTriangle className="text-red-500 w-10 h-10" />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
          {title}
        </h2>

        {/* Message */}
        <p className="text-gray-500 dark:text-zinc-400 mb-6 max-w-md mx-auto">{message}</p>

        {/* Action Buttons */}
        <div className="flex gap-4 justify-center flex-wrap">
          <button
            onClick={onReset}
            className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-teal-500 text-white font-semibold rounded-lg shadow-lg shadow-cyan-500/20 hover:from-cyan-600 hover:to-teal-600 transition-all flex items-center gap-2"
          >
            <RefreshCw size={18} />
            Zkusit znovu
          </button>

          {showHomeButton && (
            <button
              onClick={onGoHome}
              className="px-6 py-2 border border-cyan-500 text-cyan-500 font-semibold rounded-lg hover:bg-cyan-500/10 transition-all flex items-center gap-2"
            >
              <Home size={18} />
              Hlavní stránka
            </button>
          )}
        </div>

        {/* Development Error Details */}
        {isDev && error && (
          <div className="mt-8 p-4 rounded-lg bg-red-500/5 border border-red-500/20 text-left">
            <h3 className="text-red-500 font-bold text-sm mb-2">
              Detaily chyby (pouze v development módu):
            </h3>
            <pre className="font-mono text-xs text-gray-500 whitespace-pre-wrap break-words">
              {error.toString()}
              {errorInfo?.componentStack && (
                <>
                  {'\n\nComponent Stack:'}
                  {errorInfo.componentStack}
                </>
              )}
            </pre>
          </div>
        )}
      </Card>
    </div>
  );
}

// Export the class component
export const ErrorBoundary = ErrorBoundaryClass;

// Export a hook-friendly wrapper for functional components
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>,
) {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithErrorBoundary = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  ComponentWithErrorBoundary.displayName = `withErrorBoundary(${displayName})`;

  return ComponentWithErrorBoundary;
}

// Specialized error boundaries for different sections
export const DashboardErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ErrorBoundary
    title="Chyba v dashboardu"
    message="Nepodařilo se načíst dashboard. Data čidel mohou být dočasně nedostupná."
    showHomeButton={false}
  >
    {children}
  </ErrorBoundary>
);

export const SensorErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ErrorBoundary
    title="Chyba při zobrazení čidla"
    message="Nepodařilo se zobrazit data čidla. Zkuste obnovit stránku."
    showHomeButton={true}
  >
    {children}
  </ErrorBoundary>
);

export const ChartErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ErrorBoundary
    title="Chyba při vykreslování grafu"
    message="Graf se nepodařilo vykreslit. Data mohou být poškozená nebo nedostupná."
    showHomeButton={false}
  >
    {children}
  </ErrorBoundary>
);
